import * as i0 from '@angular/core';
import { Injectable, Inject, ElementRef, Renderer2, Directive, Self, Input, Output, NgModule } from '@angular/core';
import { TIPTAP_EDITOR, INITIALIZATION_TIPTAP_CONTAINER } from '@taiga-ui/addon-editor/tokens';
import * as i3 from '@taiga-ui/cdk';
import { tuiPx, TuiDestroyService } from '@taiga-ui/cdk';
import { startWith, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as i1$1 from '@taiga-ui/addon-editor/abstract';
import { AbstractTuiEditor } from '@taiga-ui/addon-editor/abstract';
import { EDITOR_BLANK_COLOR } from '@taiga-ui/addon-editor/constants';
import { tuiParseStyle, tuiGetMarkRange } from '@taiga-ui/addon-editor/utils';
import { EditorState } from 'prosemirror-state';
import * as i1 from 'rxjs';

function tuiIsEmptyParagraph(json) {
    return (Array.isArray(json) &&
        json.length === 1 &&
        json[0].type === `paragraph` &&
        !json[0].hasOwnProperty(`content`));
}

class TuiTiptapEditorService extends AbstractTuiEditor {
    constructor(editorRef) {
        super();
        this.editorRef = editorRef;
        this.editorRef.subscribe(editor => {
            this.editor = editor;
            editor.on(`transaction`, () => {
                this.stateChange$.next();
            });
            editor.on(`update`, () => {
                const content = editor.getHTML();
                const json = editor.getJSON().content;
                const value = tuiIsEmptyParagraph(json) ? `` : content;
                this.valueChange$.next(value);
            });
        });
    }
    get isFocused() {
        return this.editor.isFocused;
    }
    get html() {
        return this.editor.getHTML();
    }
    get editable() {
        return this.editor.isEditable;
    }
    set editable(editable) {
        this.editor.setEditable(editable);
    }
    get state() {
        return this.editor.state;
    }
    getOriginTiptapEditor() {
        return this.editor;
    }
    undoDisabled() {
        return !this.editor.can().undo();
    }
    redoDisabled() {
        return !this.editor.can().redo();
    }
    getFontColor() {
        return this.editor.getAttributes(`textStyle`).fontColor || EDITOR_BLANK_COLOR;
    }
    getFontSize() {
        return parseInt(this.editor.getAttributes(`textStyle`).fontSize, 10);
    }
    setFontSize(size) {
        this.editor
            .chain()
            .setMark(`textStyle`, { fontSize: tuiPx(size) })
            .run();
    }
    getBackgroundColor() {
        var _a;
        return ((_a = this.editor) === null || _a === void 0 ? void 0 : _a.getAttributes(`textStyle`).backgroundColor) || `transparent`;
    }
    getCellColor() {
        return (this.editor.getAttributes(`tableCell`).background ||
            this.editor.getAttributes(`tableHeader`).background);
    }
    getGroupColor() {
        var _a, _b, _c, _d;
        if (this.editor.isActive(`group`)) {
            const style = (_b = (_a = this.editor.getAttributes(`group`)) === null || _a === void 0 ? void 0 : _a.style) !== null && _b !== void 0 ? _b : ``;
            const styles = tuiParseStyle(style);
            return (_d = (_c = styles[`background-color`]) !== null && _c !== void 0 ? _c : styles[`background`]) !== null && _d !== void 0 ? _d : ``;
        }
        return ``;
    }
    onAlign(align) {
        this.editor.chain().focus().setTextAlign(align).run();
    }
    setImage(src) {
        this.editor
            .chain()
            .focus()
            .command(({ commands, state }) => {
            var _a;
            const setImage = ((_a = commands.setEditableImage) !== null && _a !== void 0 ? _a : commands.setImage);
            if (setImage) {
                const anchor = state.selection.anchor;
                setImage({ src });
                commands.setTextSelection(anchor);
                return true;
            }
            return false;
        })
            .run();
    }
    undo() {
        this.editor.chain().undo().run();
    }
    redo() {
        this.editor.chain().redo().run();
    }
    setHorizontalRule() {
        this.editor.chain().focus().setHorizontalRule().run();
    }
    removeFormat() {
        this.editor.commands.unsetAllMarks();
        this.editor.commands.clearNodes();
    }
    setFontColor(color) {
        this.editor.chain().focus().setFontColor(color).run();
    }
    setBackgroundColor(color) {
        this.editor.chain().focus().setBackgroundColor(color).run();
    }
    toggleUnderline() {
        this.editor.chain().focus().toggleUnderline().run();
    }
    toggleStrike() {
        this.editor.chain().focus().toggleStrike().run();
    }
    toggleOrderedList() {
        this.editor.chain().focus().toggleOrderedList().run();
    }
    toggleUnorderedList() {
        this.editor.chain().focus().toggleBulletList().run();
    }
    togglePre() {
        this.editor.chain().focus().toggleCodeBlock().run();
    }
    sinkListItem() {
        this.editor.chain().focus().sinkListItem(`listItem`).run();
    }
    liftListItem() {
        this.editor.chain().focus().liftListItem(`listItem`).run();
    }
    isActive(nameOrAttributes) {
        return this.editor.isActive(nameOrAttributes);
    }
    isActive$(nameOrAttributes) {
        return this.stateChange$.pipe(startWith(null), map(() => this.isActive(nameOrAttributes)), distinctUntilChanged());
    }
    toggleBold() {
        this.editor.chain().focus().toggleBold().run();
    }
    toggleCode() {
        this.editor.chain().focus().toggleCode().run();
    }
    toggleItalic() {
        this.editor.chain().focus().toggleItalic().run();
    }
    toggleBlockquote() {
        this.editor.chain().focus().toggleBlockquote().run();
    }
    toggleSubscript() {
        this.editor.chain().focus().toggleSubscript().run();
    }
    toggleSuperscript() {
        this.editor.chain().focus().toggleSuperscript().run();
    }
    toggleCodeBlock() {
        this.editor.chain().focus().toggleCodeBlock().run();
    }
    insertTable(cols, rows) {
        this.editor.chain().focus().insertTable({ cols, rows }).run();
    }
    addColumnAfter() {
        this.editor.chain().focus().addColumnAfter().run();
    }
    addColumnBefore() {
        this.editor.chain().focus().addColumnBefore().run();
    }
    addRowAfter() {
        this.editor.chain().focus().addRowAfter().run();
    }
    addRowBefore() {
        this.editor.chain().focus().addRowBefore().run();
    }
    deleteColumn() {
        this.editor.chain().focus().deleteColumn().run();
    }
    deleteRow() {
        this.editor.chain().focus().deleteRow().run();
    }
    mergeCells() {
        this.editor.chain().focus().mergeCells().run();
    }
    splitCell() {
        this.editor.chain().focus().splitCell().run();
    }
    canMergeCells() {
        return this.editor.can().mergeCells();
    }
    canSplitCells() {
        return this.editor.can().splitCell();
    }
    setHeading(level) {
        this.editor.chain().focus().setHeading({ level }).run();
    }
    setParagraph(options) {
        this.editor.chain().focus().setParagraph().run();
        if (options) {
            this.editor.chain().setMark(`textStyle`, options).run();
        }
    }
    setHardBreak() {
        this.editor.chain().setHardBreak().run();
    }
    setTextSelection(value) {
        this.editor.commands.setTextSelection(value);
    }
    toggleLink(href) {
        this.editor.chain().focus().toggleLink({ href }).run();
    }
    setLink(href) {
        this.editor.chain().focus().setLink({ href }).run();
    }
    unsetLink() {
        this.editor.chain().focus().unsetLink().run();
    }
    focus() {
        this.editor.chain().focus().run();
    }
    setValue(value) {
        if (value === this.html || (value === `` && this.html === `<p></p>`)) {
            return;
        }
        this.editor.commands.setContent(value);
        this.editor.view.updateState(EditorState.create({
            schema: this.editor.state.schema,
            doc: this.editor.state.doc,
            selection: this.editor.state.selection,
            storedMarks: this.editor.state.storedMarks,
            plugins: this.editor.state.plugins,
        }));
    }
    destroy() {
        this.editor.destroy();
    }
    setCellColor(color) {
        this.editor.chain().focus().setCellBackground(color).run();
    }
    selectClosest() {
        const pos = this.editor.state.selection.anchor;
        const { schema, doc } = this.editor.state;
        const range = tuiGetMarkRange(doc.resolve(pos), schema.marks.link);
        if (range) {
            this.editor.chain().setTextSelection(range).run();
        }
    }
    enter() {
        this.editor.commands.enter();
    }
    setDetails() {
        this.editor.commands.setDetails();
    }
    removeDetails() {
        this.editor.commands.removeDetails();
    }
    setGroup() {
        this.editor.commands.setGroup();
    }
    removeGroup() {
        this.editor.commands.removeGroup();
    }
    setGroupHilite(color) {
        this.editor.commands.setGroupHilite(color);
    }
    setAnchor(anchor) {
        this.editor.commands.setAnchor(anchor.replace(`#`, ``));
    }
    removeAnchor() {
        this.editor.commands.removeAnchor();
    }
    setFileLink(preview) {
        this.editor.commands.setFileLink(preview);
    }
    setYoutubeVideo(options) {
        this.editor.commands.setYoutubeVideo(options);
    }
    setIframe(options) {
        this.editor.commands.setIframe(options);
    }
    removeEmptyTextStyle() {
        this.editor.commands.removeEmptyTextStyle();
    }
    toggleMark(typeOrName, attributes, options) {
        this.editor.commands.toggleMark(typeOrName, attributes, options);
    }
}
TuiTiptapEditorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorService, deps: [{ token: TIPTAP_EDITOR }], target: i0.ɵɵFactoryTarget.Injectable });
TuiTiptapEditorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TIPTAP_EDITOR]
                }] }]; } });

class TuiTiptapEditorDirective {
    constructor(el, renderer, editor, editorContainer, editorLoaded$, destroy$) {
        this.el = el;
        this.renderer = renderer;
        this.editor = editor;
        this.editorContainer = editorContainer;
        this.editorLoaded$ = editorLoaded$;
        this.valueChange = this.editor.valueChange$;
        this.stateChange = this.editor.stateChange$;
        this.editorLoaded$.pipe(takeUntil(destroy$)).subscribe(() => {
            this.renderer.appendChild(this.el.nativeElement, this.editorContainer);
        });
    }
    set value(value) {
        this.editor.setValue(value);
    }
    set editable(editable) {
        this.editor.editable = editable;
    }
}
TuiTiptapEditorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorDirective, deps: [{ token: ElementRef }, { token: Renderer2 }, { token: TuiTiptapEditorService }, { token: INITIALIZATION_TIPTAP_CONTAINER }, { token: TIPTAP_EDITOR }, { token: TuiDestroyService, self: true }], target: i0.ɵɵFactoryTarget.Directive });
TuiTiptapEditorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: TuiTiptapEditorDirective, selector: "[tuiTiptapEditor]", inputs: { value: "value", editable: "editable" }, outputs: { valueChange: "valueChange", stateChange: "stateChange" }, providers: [TuiDestroyService], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tuiTiptapEditor]',
                    providers: [TuiDestroyService],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [Renderer2]
                }] }, { type: i1$1.AbstractTuiEditor, decorators: [{
                    type: Inject,
                    args: [TuiTiptapEditorService]
                }] }, { type: HTMLElement, decorators: [{
                    type: Inject,
                    args: [INITIALIZATION_TIPTAP_CONTAINER]
                }] }, { type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TIPTAP_EDITOR]
                }] }, { type: i3.TuiDestroyService, decorators: [{
                    type: Self
                }, {
                    type: Inject,
                    args: [TuiDestroyService]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], editable: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], stateChange: [{
                type: Output
            }] } });

class TuiTiptapEditorModule {
}
TuiTiptapEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiTiptapEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorModule, declarations: [TuiTiptapEditorDirective], exports: [TuiTiptapEditorDirective] });
TuiTiptapEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTiptapEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TuiTiptapEditorDirective],
                    exports: [TuiTiptapEditorDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiTiptapEditorDirective, TuiTiptapEditorModule, TuiTiptapEditorService };
